// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
// NGRX
import { Store } from '@ngrx/store';
// Translate
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
// RxJS
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Auth
import { AuthNoticeService, AuthService, Register, User } from '../../../../core/auth/';
import {
	getPasswordErrorMessage,
	getConfirmPasswordErrorMessage,
	getOtpErrorMessage
} from '../../../../core/auth/_utils/auth-validation.util';
import { AppState } from '../../../../core/reducers';
import { ConfirmPasswordValidator } from '../register/confirm-password.validator';
import { RecaptchaErrorParameters } from "ng-recaptcha";
import { environment } from '../../../../../environments/environment';
import CryptoES from 'crypto-es';
// import {NgxCaptchaModule,ReCaptcha2Component} from 'ngx-captcha'
@Component({
	selector: 'kt-reset-password',
	templateUrl: './reset-password.component.html',
	styleUrls: ['./reset-password.component.scss']
})

export class ResetPasswordComponent implements OnInit, OnDestroy {
	resetForm: UntypedFormGroup;
	loading = false;
	errors: any = [];
	siteKey = environment.runtime.siteKey
	isCaptchaSelected: boolean = true
	private unsubscribe: Subject<any>; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
	gCaptchaResponse: any;
	script: any;
	recaptcha: boolean;
	isLogoAvailable: any;
	email: string = '';
	resendCountdown: number = 30;
	resendInterval: any;
	otpErrorMessage: string = '';
	passwordErrorMessage: string = '';
	confirmPasswordErrorMessage: string = '';
	imagePaths = {
		uptimeLogoOriginal: "assets/media/logos/logo-loader-colour.svg"
	}

	/**
	 * Component constructor
	 *
	 * @param authNoticeService: AuthNoticeService
	 * @param translate: TranslateService
	 * @param router: Router
	 * @param auth: AuthService
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 */
	constructor(
		private readonly authNoticeService: AuthNoticeService,
		private readonly translate: TranslateService,
		private readonly router: Router,
		private readonly auth: AuthService,
		private readonly store: Store<AppState>,
		private readonly fb: UntypedFormBuilder,
		private readonly cdr: ChangeDetectorRef
	) {
		this.unsubscribe = new Subject();
	}

	/*
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	*/

	/**
	 * On init
	 */
	ngOnInit() {
		this.recaptcha = environment.runtime.recaptcha;
		this.isLogoAvailable = JSON.parse(localStorage.getItem('layoutConfig'))['logo'];
		const storedEmail = sessionStorage.getItem('femail');
		if (storedEmail) {
			this.email = storedEmail;
			this.initResetForm();
			this.startResendTimer();

			this.resetForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
				this.updateErrors();
			});
			this.updateErrors();
		} else {
			this.router.navigateByUrl('/web/forgot-password');
		}
	}

	/*
	* On destroy
	*/
	ngOnDestroy(): void {
		if (this.resendInterval) {
			clearInterval(this.resendInterval);
		}
		this.unsubscribe.next(undefined);
		this.unsubscribe.complete();
		this.loading = false;
	}



	/**
	 * Form initalization
	 * Default params, validators
	 */
	initResetForm() {
		this.resetForm = this.fb.group({
			email: [sessionStorage.getItem('femail'), Validators.compose([
				// Validators.required,
				Validators.email,
				Validators.minLength(3),
				// https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
				Validators.maxLength(320)
			]),
			],
			otp: ['', Validators.compose([
				Validators.required,
				Validators.minLength(6),
				Validators.maxLength(6)
			]),
			],
			password: ['', Validators.compose([
				Validators.required,
				Validators.maxLength(100),
				Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}')
			])],
			confirmPassword: ['', Validators.compose([
				Validators.required,
				Validators.maxLength(100),
				Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}')
			])],
		}, {
			validator: ConfirmPasswordValidator.MatchPassword
		});

		this.resetForm.get('otp').valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			const otpControl = this.resetForm.get('otp');
			if (otpControl?.hasError('invalidReset')) {
				const { invalidReset, ...errors } = otpControl.errors || {};
				otpControl.setErrors(Object.keys(errors).length ? errors : null);
			}
		});

	}

	/**
	 * Form Submit
	 */
	submit() {
		const controls = this.resetForm.controls;

		// check form
		if (this.resetForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			this.updateErrors();
			return;
		}

		this.loading = true;

		const _user: User = new User();
		_user.clear();
		_user.email = controls['email'].value.toLowerCase();
		_user.otp = controls['otp'].value;
		const encryptedPassword = CryptoES.AES.encrypt(controls['password'].value, environment.runtime.authTokenKey).toString();
		_user.password = encryptedPassword;
		_user['g-recaptcha-response'] = this.gCaptchaResponse
		this.auth.resetPassword(_user).pipe(
			tap(user => {
				if (user.body.status == 200) {
					this.authNoticeService.setNotice(this.translate.instant('AUTH.RESET.SUCCESS'), 'success');
					this.router.navigateByUrl('/web/login');
				} else {
					if (environment.runtime.recaptcha)
						grecaptcha.reset()
					this.isCaptchaSelected = true;
					const otpControl = this.resetForm.get('otp');
					if (otpControl) {
						otpControl.setErrors({ invalidReset: true });
						otpControl.markAsTouched();
						this.updateErrors();
					}
				}
			}),
			takeUntil(this.unsubscribe),
			finalize(() => {
				this.loading = false;
				this.cdr.detectChanges();
			})
		).subscribe();
	}

	resolved(e) {
		this.gCaptchaResponse = e
		this.isCaptchaSelected = e ? false : true;
	}

	updateErrors(): void {
		this.otpErrorMessage = getOtpErrorMessage(this.resetForm.get('otp'), this.translate);
		this.passwordErrorMessage = getPasswordErrorMessage(this.resetForm.get('password'), this.translate, this.translate.instant('AUTH.RESET.NEWPASSWORD'));
		this.confirmPasswordErrorMessage = getConfirmPasswordErrorMessage(this.resetForm.get('confirmPassword'), this.translate);
	}

	startResendTimer() {
		this.resendCountdown = 30;
		if (this.resendInterval) {
			clearInterval(this.resendInterval);
		}
		this.resendInterval = setInterval(() => {
			if (this.resendCountdown > 0) {
				this.resendCountdown--;
				this.cdr.detectChanges();
			} else {
				clearInterval(this.resendInterval);
			}
		}, 1000);
	}

	resendOTP() {
		if (this.resendCountdown > 0) return;
		this.loading = true;
		this.auth.requestPassword(this.email.toLowerCase(), this.gCaptchaResponse).pipe(
			tap(response => {
				if (response.status == 200) {
					this.startResendTimer();
				} else {
					if (this.recaptcha) {
						grecaptcha.reset();
					}
					this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.NOT_FOUND', { name: this.translate.instant('AUTH.INPUT.EMAIL') }), 'danger');
				}
			}),
			takeUntil(this.unsubscribe),
			finalize(() => {
				this.loading = false;
				this.cdr.detectChanges();
			})
		).subscribe();
	}

	goToForgotPassword() {
		this.router.navigateByUrl('/web/forgot-password');
	}
}
